import { useCallback, useEffect, useState } from "react";
import { Box, Button, Typography, Grid, InputLabel, Fade } from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router";
import { useSnackbar } from "notistack";
import BrandCarousel from "../login/components/BrandCarousel";
import { useAppSelector, useAppDispatch } from "../../redux/store";
import { resetState, resetPassword } from "../../redux/slices/loginSlice";
import PasswordField from "../../components/PasswordField";

const ResetPasswordForm = () => {
  const [fadeIn, setFadeIn] = useState(false);

  const { loading, resetPasswordSuccess, resetPasswordError } = useAppSelector(
    (state) => state.login,
  ) as any;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    setFadeIn(true);

    if (resetPasswordSuccess) {
      enqueueSnackbar("Password reset successfully", {
        variant: "success",
      });

      setFadeIn(false);
      setTimeout(() => {
        navigate("/login");
      }, 1000);
    }

    if (resetPasswordError) {
      enqueueSnackbar(resetPasswordError, { variant: "error" });
    }
  }, [
    dispatch,
    setFadeIn,
    navigate,
    resetPasswordSuccess,
    enqueueSnackbar,
    resetPasswordError,
  ]);

  useEffect(() => {
    return () => {
      dispatch(resetState());
    };
  }, [dispatch]);

  const handleSubmit = (e: any) => {
    e.preventDefault();

    const searchParams = new URLSearchParams(location.search);
    const email = searchParams.get("email")?.replace(/ +/g, "+");
    const resetToken = searchParams.get("token");

    if (email && resetToken) {
      const newPassword = e.target.newPassword.value;
      dispatch(resetPassword({ email, resetToken, newPassword }));
    }
  };

  const handleBackToLogin = useCallback(
    (e: any) => {
      e.preventDefault();

      setFadeIn(false);

      dispatch(resetState());

      setTimeout(() => {
        navigate("/login");
      }, 1000);
    },
    [dispatch, navigate, setFadeIn],
  );

  return (
    <Fade in={fadeIn}>
      <Grid
        sx={{ width: "100vw", height: "100vh", overflow: "auto" }}
        container
      >
        <Grid
          size={{ sm: 12, md: 6 }}
          sx={{ display: "flex", alignItems: "center", p: 2 }}
        >
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              maxWidth: 350,
              mx: "auto",
              "& .MuiOutlinedInput-input": {
                p: "4.5px 14px",
              },
              "& .MuiOutlinedInput-root": {
                borderRadius: 0,
              },
              "& .MuiTextField-root": {
                mt: 0,
              },
              "& .MuiButton-root": {
                borderRadius: 0,
                p: "2.75px 15px",
              },
            }}
          >
            <BrandCarousel />
            <Typography variant="h4" sx={{ fontSize: 24 }} gutterBottom>
              Reset Your Password
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: 15, mb: 2 }}
              gutterBottom
            >
              Provide a new password for your account.
            </Typography>

            <InputLabel htmlFor="newPassword" sx={{ fontSize: 14 }}>
              New Password
            </InputLabel>
            <PasswordField
              id="newPassword"
              variant="outlined"
              size="small"
              fullWidth
              margin="normal"
              // autoComplete="current-password"
              disabled={loading || resetPasswordSuccess}
              error={resetPasswordError?.toLowerCase()?.includes("password")}
              placeholder="Enter your new password"
            />
            <Button
              sx={{ mt: 2 }}
              variant="contained"
              color="primary"
              fullWidth
              type="submit"
              disabled={loading || resetPasswordSuccess}
            >
              Reset Password
            </Button>

            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <Typography
                variant="body2"
                color="primary"
                sx={{ mt: 2, fontSize: 14 }}
              >
                Or go back to{" "}
                <Link
                  onClick={handleBackToLogin}
                  to="/login"
                  style={{
                    textDecoration: "none",
                    color: "inherit",
                    fontWeight: "bold",
                    fontFamily: "Brandon_Grotesque_bold",
                  }}
                >
                  Log In
                </Link>
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid
          size={{ sm: 12, md: 6 }}
          sx={{
            display: { xs: "none", md: "block" },
            height: "100vh",
            overflow: "hidden",
            borderRadius: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              style={{
                height: "100vh",
                width: "100%",
                objectFit: "cover",
                objectPosition: "center",
                display: "block",
              }}
              src="/forgot_reset_img.jpg"
              alt="Logo"
            />
          </Box>
        </Grid>
      </Grid>
    </Fade>
  );
};

export default ResetPasswordForm;
